import { useState } from "react";
import { useSettingsContext } from "./useSettingsContext";
import { useAuthContext } from "./useAuthContext";
import { useAccountContext } from "./useAccountContext";
import axios from 'axios';
import { useTranslation } from 'react-i18next';

export const useSettings = () => {

    const { t } = useTranslation();
    const [error, setError] = useState(undefined);
    const { dispatch, isWinAuth: winAuth } = useSettingsContext();
    const [isLoading, setIsLoading] = useState(false);
    const { username, user, clientApi, dispatch: authDispatch } = useAuthContext();
    const { account } = useAccountContext();
    const axiosInstance = axios.create({
        baseURL: `${clientApi}`,
        headers: {
            'accepts': 'application/json',
            'Content-Type': 'application/json',
        },
    });

    axiosInstance.interceptors.request.use(request => {
        // console.log('Set Bearer Token')
        const accessToken = user.token;
        if (accessToken) {
            request.headers['Authorization'] = `Bearer ${accessToken}`;
        }
        return request;
    }, error => {
        return Promise.reject(error);
    });

    axiosInstance.interceptors.response.use(
        response => response, // Directly return successful responses.
        async error => {
            // console.log('onLeave Refresh Access Token Expired!!!')
            const originalRequest = error.config;
            if (error.response.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true; // Mark the request as retried to avoid infinite loops.
                try {
                    if (!winAuth) {
                        // Make a request to your auth server to refresh the token.
                        const response = await axiosInstance.post(`${clientApi}api/auth/refresh`, user);
                        // Store the new access and refresh tokens.
                        authDispatch({ type: 'Refresh', payload: response.data, clientApi: clientApi, username: username });

                        // Update the authorization header with the new access token.
                        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
                    }
                    else {
                        const newToken = await acquireSilentToken();
                        let tmpUser = user;
                        tmpUser.token = newToken;
                        authDispatch({ type: 'REFRESH', payload: tmpUser, clientApi: clientApi, username: username });
                        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                    }
                    return axios(originalRequest); // Retry the original request with the new access token.
                } catch (refreshError) {
                    // Handle refresh token errors by clearing stored tokens and redirecting to the login page.
                    console.error('Token refresh failed:', refreshError);
                    authDispatch({ type: 'LOGOUT' });
                    return Promise.reject(refreshError);
                }
            }
            return Promise.reject(error); // For all other errors, return the error as is.
        }
    );

    // * Retrieving Auth method on login in order to determine which sign in method to use 
    // * Because it happens on login do not use axios instance because there is no token 
    // * Currently:
    // *           false -> JWT token DB Username and Password 
    // *           true  -> Azure Auth 
    const isWinAuth = async (api) => {
        let ret = false;
        await axios.get(`${api}api/Settings/authType`).then((resp) => {
            ret = resp.data
            dispatch({ type: 'UPDATE_WINAUTH', payload: resp.data })
        }).catch((error) => { console.log(error) })
        return ret;
    }

    const getSettings = async () => {
        setIsLoading(true)
        await axiosInstance.get(`${clientApi}api/Settings/AdminSettings`, {
            params: {
                username: username,
            }
        })
            .then((response) => {
                // * This gets from OASISPARAM which is always single line
                if (response.status === 200) {
                    dispatch({ type: 'UPDATE_SETTINGS', payload: response.data[0] })
                }
            })
            .catch((error) => {
                console.log(error.response);
                setError(error)
            });
        setIsLoading(false)
    };

    const getEmployeeTabs = async () => {
        setIsLoading(true)
        await axiosInstance.get(`${clientApi}api/Settings/GetEmployeeTabsSettings`, {
            params: {
                username: username,
            }
        })
            .then((response) => {
                // * This gets from OASISPARAM which is always single line
                const { employmentTabData, miscellaneousTabData, additionalTabData, personalTabData } = createTabDataObjects(response.data)
                setIsLoading(false)
                if (response.status === 200) {
                    dispatch({ type: 'UPDATE_EMPLOYEETABS', payload: { employmentTabData, miscellaneousTabData, additionalTabData, personalTabData } })
                }
            })
            .catch((error) => {
                console.log(error.response);
                setIsLoading(false)
                setError(error)
            });
    };

    const saveEmployeeTabs = async (sectionData) => {
        if (sectionData === undefined) return;
        setIsLoading(true);
        await axiosInstance
            .put(`${clientApi}api/Settings/saveEmployeeTabs`, createFieldsArrayFromSectionData(sectionData), {
                params: { username: account.username },
            })
            .then((response) => {
                if (response.status === 200) {
                    ret = true;
                }
                else
                    ret = false;
            })
            .catch((error) => {
                console.log(error);
                ret = false;
            });

        setIsLoading(false);
        return ret;
    };

    const getTrainingTabs = async () => {
        setIsLoading(true)
        await axiosInstance.get(`${clientApi}api/Settings/GetTrainingTabsSettings`, {
            params: { username: username }
        })
            .then((response) => {
                // * This gets from OASISPARAM which is always single line
                const { courseDetailsTabData, participantsTabData } = createTRNTabDataObjects(response.data)
                setIsLoading(false)
                if (response.status === 200) {
                    dispatch({ type: 'UPDATE_TRAININGTABS', payload: { courseDetailsTabData, participantsTabData } })
                }
            })
            .catch((error) => {
                //console.log(error.response);
                setIsLoading(false)
                setError(error)
            });
    };

    // * This is inner mapping function 
    function createTabDataObjects(fieldsArray) {
        const employmentTabData = {};
        const miscellaneousTabData = {};
        const additionalTabData = {};
        const personalTabData = {};

        fieldsArray.forEach(field => {
            // Extract tabName and window key:
            const { tabName, window, type, options, isDisabled, isVisible } = field;

            // Build the field config object:
            const fieldConfig = {
                type: type || "", // fallback to empty string if null/empty
                isDisabled: !!isDisabled,
                isVisible: !!isVisible,
            };

            // Include options if they exist and are non-null:
            if (options && Array.isArray(JSON.parse(options))) {
                fieldConfig.options = JSON.parse(options);
            }

            // Handle exceptional keys if needed (like title, icon, pattern)
            // Since these aren't present in the input array, you may need logic 
            // to inject them based on `window` name if required,
            // or this can be extended later on.

            // Assign fieldConfig to the appropriate tab object:
            switch (tabName) {
                case "employment":
                    employmentTabData[window] = fieldConfig;
                    break;
                case "miscellaneous":
                    miscellaneousTabData[window] = fieldConfig;
                    break;
                case "additional":
                    additionalTabData[window] = fieldConfig;
                    break;
                case "personal":
                    personalTabData[window] = fieldConfig;
                    break;
                default:
                    // Unknown tabName, ignore or handle as needed
                    break;
            }
        });

        // console.log(employmentTabData, miscellaneousTabData, additionalTabData, personalTabData)
        return { employmentTabData, miscellaneousTabData, additionalTabData, personalTabData };
    }

    function createTRNTabDataObjects(fieldsArray) {
        const courseDetailsTabData = {};
        const participantsTabData = {};

        fieldsArray.forEach(field => {
            // Extract tabName and window key:
            const { tabName, window, type, options, isDisabled, isVisible } = field;

            // Build the field config object:
            const fieldConfig = {
                type: type || "", // fallback to empty string if null/empty
                isDisabled: !!isDisabled,
                isVisible: !!isVisible,
            };

            // Include options if they exist and are non-null:
            if (options && Array.isArray(JSON.parse(options))) {
                fieldConfig.options = JSON.parse(options);
            }
            
            // Assign fieldConfig to the appropriate tab object:
            switch (tabName) {
                case "coursedetails":
                    courseDetailsTabData[window] = fieldConfig;
                    break;
                case "participants":
                    participantsTabData[window] = fieldConfig;
                    break;
                default:
                    // Unknown tabName, ignore or handle as needed
                    break;
            }
        });

        return { courseDetailsTabData, participantsTabData };
    }

    function createFieldsArrayFromSectionData(sectionData) {
        const fieldsArray = [];

        const tabNameMap = {
            'Employment Tab': 'employment',
            'Personal Data Tab': 'personal',
            'Additional Tab': 'additional',
            'Miscellaneous Tab': 'miscellaneous'
        };

        sectionData.forEach(section => {
            const tabName = tabNameMap[section.title] || '';

            section.data.forEach(item => {
                const field = {
                    RowGuid: crypto.randomUUID ? crypto.randomUUID() : generateUUID(), // fallback UUID gen if needed
                    TabName: tabName,
                    Window: item.window,
                    Type: item.type || '',
                    Options: item.options && Array.isArray(item.options) ? JSON.stringify(item.options) : '',
                    IsDisabled: !!item.isDisabled,
                    IsVisible: !!item.isVisible,
                };
                fieldsArray.push(field);
            });
        });

        return fieldsArray;
    }

    // Example fallback UUID generator if crypto.randomUUID is not available
    function generateUUID() {
        // Generates RFC4122 version 4 compliant UUID
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    return { getSettings, getEmployeeTabs, getTrainingTabs, isWinAuth, saveEmployeeTabs, error, isLoading }
}